import { createFileRoute } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import { BackButton } from "@/components/site/BackButton";
import { localBusinessJsonLd } from "@/lib/seo";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact — KAKAIYS" },
      {
        name: "description",
        content:
          "Get in touch with the KAKAIYS atelier. Customer care, press, wholesale and bespoke inquiries.",
      },
      { property: "og:title", content: "Contact — KAKAIYS" },
      { property: "og:description", content: "Reach the KAKAIYS atelier — we reply within 24 hours." },
      { property: "og:url", content: "https://kakaiys.com/contact" },
    ],
    links: [{ rel: "canonical", href: "https://kakaiys.com/contact" }],
    scripts: [
      { type: "application/ld+json", children: localBusinessJsonLd() },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.email.includes("@") || form.message.trim().length < 10) {
      toast.error("Please complete all fields with a valid message.");
      return;
    }
    setSending(true);
    // Wire to edge function later — for now graceful UX
    await new Promise((r) => setTimeout(r, 700));
    setSending(false);
    toast.success("Message received. Our atelier will reply within 24 hours.");
    setForm({ name: "", email: "", subject: "", message: "" });
  };

  return (
    <div className="pt-40 pb-24 container-luxe">
      <BackButton fallback="/" className="mb-8" />
      <div className="max-w-3xl mx-auto text-center mb-16">
        <div className="eyebrow mb-5">Maison · Contact</div>
        <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] mb-6">Speak with the atelier</h1>
        <p className="text-foreground/75 text-lg">
          Whether it is a question about your order, a bespoke request, or a press inquiry — we read every
          message and reply within 24 hours.
        </p>
      </div>

      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-14 max-w-5xl mx-auto">
        {/* Info */}
        <div className="space-y-8">
          <ContactItem icon={<Mail className="h-4 w-4" />} title="Customer Care" detail="care@kakaiys.com" sub="Order, sizing, shipping" />
          <ContactItem icon={<MessageCircle className="h-4 w-4" />} title="Bespoke & Wholesale" detail="atelier@kakaiys.com" sub="Made to order, B2B" />
          <ContactItem icon={<MapPin className="h-4 w-4" />} title="The Atelier" detail="Sialkot, Punjab — Pakistan" sub="Visits by appointment only" />
          <ContactItem icon={<Clock className="h-4 w-4" />} title="Hours" detail="Mon — Sat · 09:00 — 19:00 PKT" sub="Replies within 24h" />
        </div>

        {/* Form */}
        <form onSubmit={onSubmit} className="space-y-5 border border-gold/20 bg-card/40 p-8">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
          </div>
          <Field label="Subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} />
          <div>
            <label className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground">Message</label>
            <textarea
              required
              rows={6}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full mt-2 bg-transparent border-b border-gold/30 focus:border-gold outline-none py-2 text-sm resize-none"
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            className="w-full text-[11px] uppercase tracking-[0.28em] bg-gold text-ink py-4 hover:bg-gold/90 transition-colors disabled:opacity-60"
          >
            {sending ? "Sending…" : "Send message"}
          </button>
        </form>
      </div>
    </div>
  );
}

function ContactItem({
  icon,
  title,
  detail,
  sub,
}: {
  icon: React.ReactNode;
  title: string;
  detail: string;
  sub: string;
}) {
  return (
    <div className="flex gap-4 border-b border-gold/15 pb-6">
      <div className="h-9 w-9 inline-flex items-center justify-center border border-gold/30 text-gold/80 shrink-0">
        {icon}
      </div>
      <div>
        <div className="eyebrow text-gold/80 mb-1">{title}</div>
        <div className="font-serif text-lg">{detail}</div>
        <div className="text-xs text-muted-foreground mt-1">{sub}</div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground">{label}</label>
      <input
        required
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-2 bg-transparent border-b border-gold/30 focus:border-gold outline-none py-2 text-sm"
      />
    </div>
  );
}
